import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Unsubscribable } from 'rxjs';

import { WorldMapType, WorldmapUiComponent } from '@innovation-os-ng/ui/chart-ng';
import { CompoundFilter, LeafFilter } from '@itonics/filters';

import { TrackedEvents } from '../../../shared/constants/tracker-ids';
import { findLeafByOnAndExclude } from '../../../shared/helpers/filter-helper';
import { FilterChangeAction, SignalsFilters, SignalsType } from '../../../shared/model/types';
import { InsightsTrackingService } from '../../../shared/modules/shared-services/services/insights-tracking-service';
import { MetricService } from '../../../shared/modules/shared-services/services/metric.service';
import { WorldMapService } from '../../services/worldMap.service';

import { CommonModule } from '@angular/common';
import { LoaderModule } from '@innovation-os-ng/ui/loader';
import { TitleCasePipe } from '@innovation-os-ng/ui/utils';
import { TranslateModule } from '@itonics/ngx-translate-simple';
import * as d3 from 'd3';

@Component({
  selector: 'signals-worldmap',
  templateUrl: './worldmap.component.html',
  styleUrls: ['./worldmap.component.scss'],
  standalone: true,
  imports: [CommonModule, LoaderModule, TranslateModule, TitleCasePipe, WorldmapUiComponent]
})
export class WorldmapComponent implements OnInit, OnDestroy {
  private documentsFilter: CompoundFilter;
  private documentsFilterChanges: Unsubscribable;

  @Input()
  set filter(filter: CompoundFilter) {
    if (!filter) {
      return;
    }
    this.unsubscribe();
    this.documentsFilter = filter;
    this.subscribe();
    this.getAggregateData();
    this.setModel();
  }

  @Output()
  filterChanged: EventEmitter<{
    action: FilterChangeAction;
    filter: LeafFilter;
  }> = new EventEmitter();

  @Output()
  loadingChanged: EventEmitter<boolean> = new EventEmitter();

  /*
   * DATA STATES
   */

  public noData: boolean;
  private lastRequestTime: number;
  public get loading(): boolean {
    return Boolean(this.lastRequestTime);
  }

  /**
   * WORLD MAP DATA & MODEL
   */
  public data: Array<any>;
  public countriesModel: Array<string>;
  public countriesExcluded: Array<string>;

  /**
   * SCALES
   */

  public scale: (value: number) => number;

  /**
   * Aggregation
   */
  public insightsType = SignalsType;

  private metricChangeSubs: Unsubscribable;

  constructor(
    private readonly insightsTrackingService: InsightsTrackingService,
    private readonly worldMapService: WorldMapService,
    private readonly metricService: MetricService
  ) {}

  ngOnInit() {
    /*

      We subscribe to changes in the global metric
      to re-fetch data
    */
    this.metricChangeSubs = this.metricService.onMetricChange(() => {
      this.getAggregateData();
    });
  }

  ngOnDestroy() {
    this.unsubscribe();
    this.metricChangeSubs.unsubscribe();
  }

  private unsubscribe() {
    if (this.documentsFilterChanges) {
      this.documentsFilterChanges.unsubscribe();
    }
  }

  private subscribe() {
    this.documentsFilterChanges = this.documentsFilter.onChanged(() => {
      this.getAggregateData();
      this.setModel();
    });
  }

  private setModel() {
    const selectionFilter = this.documentsFilter.getQuery(SignalsFilters.SELECTION) as CompoundFilter;

    if (!selectionFilter) {
      this.countriesModel = [];
      this.countriesExcluded = [];

      return;
    }

    const inclusionFilter = selectionFilter.findQuery(findLeafByOnAndExclude(this.insightsType.COUNTRY, false));
    const exclusionFilter = selectionFilter.findQuery(findLeafByOnAndExclude(this.insightsType.COUNTRY, true));

    this.countriesModel = inclusionFilter ? ((inclusionFilter as LeafFilter).getValues() as Array<string>) : [];
    this.countriesExcluded = exclusionFilter ? ((exclusionFilter as LeafFilter).getValues() as Array<string>) : [];
  }

  private getAggregateData() {
    // The filters that we want for
    // Mark as loading with timestamp
    const timestamp = Date.now();
    this.lastRequestTime = timestamp;
    this.loadingChanged.emit(this.loading);

    // a copy of the combined filter
    const filter = this.documentsFilter.clone();
    const filterSelection = filter.getQuery(SignalsFilters.SELECTION) as CompoundFilter;
    // if there is no selection inside, nothing to highlight
    if (filterSelection) {
      // we don't want to filter down the result data with filters on Country, so we remove them from the selection
      // also we want to remove both exclusion and inclusion on "country"
      const exclusionFilter = filterSelection.findQuery(findLeafByOnAndExclude(SignalsType.COUNTRY, true)) as LeafFilter;
      const inclusionFilter = filterSelection.findQuery(findLeafByOnAndExclude(SignalsType.COUNTRY, false)) as LeafFilter;

      if (exclusionFilter) {
        filterSelection.removeFilter(exclusionFilter);
      }

      if (inclusionFilter) {
        filterSelection.removeFilter(inclusionFilter);
      }
    }

    this.worldMapService.getWorldMapAggregations(filter, this.metricService.getGlobalMetric(), 'worldMap-v2').then((response) => {
      if (this.metricService.isMetricLikeRelevance(this.metricService.getGlobalMetric().metric)) {
        // we need to use linear scale for relevance values because zero is not a value
        // that will work with this scale
        this.scale = d3.scaleLinear().domain(this.getDataRange(response)).range(this.getDataRange(response));
      } else {
        // we want logarithmic for doc counts
        this.scale = d3.scaleLog().domain(this.getDataRange(response)).range(this.getDataRange(response));
      }

      this.data = response;

      // Only end loading if this was the last request sent
      if (this.lastRequestTime === timestamp) {
        this.lastRequestTime = null;
      }
      this.loadingChanged.emit(this.loading);
      this.noData = this.data.length === 0;
    });
  }

  public countrySelected(countries: Array<string>) {
    const filter = new LeafFilter(SignalsType.COUNTRY);

    if (countries) {
      filter.setValues(countries);
    }

    this.unsubscribe();
    // to avoid calling our own callback
    this.filterChanged.next({
      action: countries && countries.length ? FilterChangeAction.ADD : FilterChangeAction.REMOVE,
      filter
    });
    this.subscribe();
  }

  /**
   * Track when user toggles a visualisation type (eg. Growth Indicators)
   *
   * @param param0 Tuple containing [property_type_changed, whether_it_was_selected (vs. deselected)]
   */
  public worldmapTypeChanged([type, selected]: [WorldMapType, boolean]) {
    this.insightsTrackingService.trackEvent(`${TrackedEvents.PREFIX_WORLDMAP_TYPE}_${type}_${selected ? 'selected' : 'deselected'}`);
  }

  //  Get data Range for scaling
  private getDataRange(data): Array<number> {
    return data.length > 0 ? d3.extent(data, (d) => d.value) : [0, 0];
  }

  /**
   * Track a generic 'visualization' event (worldmap only reference in dimension4)
   *
   * @param name Event being tracked
   */
  public trackEvent(name: string) {
    this.insightsTrackingService.trackEvent(`${TrackedEvents.PREFIX_VIZ}.${name}`, { dimension4: 'World Map' });
  }
}
